import { Box } from '@mui/material';
import { QuoteItem, QuoteStatus } from '../../common/types/quotes';
import { ReportSectionComponentType } from '../types';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReportUpdater } from '../hooks/useReportUpdater';
import EmptySectionWarning from '../components/emptySectionWarning';
import ReportSectionFiles from '../components/reportSectionFiles';
import snakeCase from 'lodash/snakeCase';
import StepFooter from '../../common/components/stepFooter';

/**
 * Report quote step
 */
const QuoteStep: ReportSectionComponentType = ({
  sectionName,
  report,
  templateFileUrl,
  onClickBack,
  onClickNext,
  isLoading: isSomethingLoading,
}) => {
  const fnaId = report.fnAs[0].id;
  const { data: quotes } = useQuery<QuoteItem[]>({
    queryKey: ['/FNAQuoteLists', { fnaID: fnaId }],
    enabled: Boolean(fnaId),
  });
  const { isLoading, saveChanges, setPropertyValue, sectionData, removeFile, addFile } =
    useReportUpdater({
      report,
      sectionName,
    });

  const handleClickNext = (): void => {
    saveChanges().then(() => onClickNext());
  };

  // Add existing quotes PDFs to the section files
  useEffect(() => {
    if (!quotes || !sectionData) {
      return;
    }

    const quotedQuotes = quotes?.filter(({ statusID }) => statusID === QuoteStatus.Quoted) ?? [];
    for (const { quotePdfUrl, partyRoleplayerName, typeNote, roleplayerName } of quotedQuotes) {
      const fileName = `${snakeCase(`${typeNote}_${roleplayerName}_${partyRoleplayerName}`)}.pdf`;
      const alreadyAdded = sectionData?.files.some(
        ({ fileUploadName }) => fileUploadName === fileName,
      );
      if (quotePdfUrl && !alreadyAdded) {
        addFile(quotePdfUrl, fileName);
      }
    }
  }, [quotes, addFile, sectionData]);

  if (!sectionData) {
    return <EmptySectionWarning />;
  }

  return (
    <Box className="space-y-4">
      <ReportSectionFiles
        addFile={addFile}
        removeFile={removeFile}
        sectionData={sectionData}
        templateFileUrl={templateFileUrl}
        setPropertyValue={setPropertyValue}
      />
      <StepFooter
        isLoading={isLoading || isSomethingLoading}
        onClickBack={onClickBack}
        onClickNext={handleClickNext}
      />
    </Box>
  );
};

export default QuoteStep;
